import { useCallback, useEffect, useState } from "react";

import { Camera, RotateCcw } from "lucide-react";
import { toast } from "sonner";

import PageHeader from "../../components/shared/PageHeader";
import Pagination from "../../components/shared/Pagination";
import SearchInput from "../../components/shared/SearchInput";
import { Button } from "../../components/ui/button";
import { useApi } from "../../context/ApiContext";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { useConfirm } from "../../context/ConfirmContext.jsx";
import { usePagination } from "../../hooks/usePagination";

import DocumentDialog from "./component/DocumentDialog";
import DocumentTable from "./component/DocumentTable";
import DocumentViewer from "./component/DocumentViewer";

const Photos = () => {
  const { user } = useAuth();
  const { projects, users, globalProjectId } = useData();
  const api = useApi();

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [selectedUser, setSelectedUser] = useState("ALL");
  const [selectedType, setSelectedType] = useState("ALL");
  const [sortBy, setSortBy] = useState("createdAt");
  const [direction, setDirection] = useState("desc");
  const [showDeleted, setShowDeleted] = useState(false);

  const pagination = usePagination(10);
  const { items: photos, currentPage: page, pageSize, setPaginationData, setCurrentPage: setPage } = pagination;

  const loadPhotos = useCallback(async () => {
    try {
      const response = await api.get(`/photos`, {
        params: {
          projectId: globalProjectId === "ALL" ? "" : globalProjectId,
          page,
          size: pageSize,
          search: searchTerm.trim() || undefined,
          startDate: dateRange.start ? new Date(dateRange.start).toISOString() : undefined,
          endDate: dateRange.end
            ? (() => {
                const d = new Date(dateRange.end);
                d.setHours(23, 59, 59, 999);
                return d.toISOString();
              })()
            : undefined,
          uploadedBy: selectedUser === "ALL" ? undefined : selectedUser,
          fileType: selectedType === "ALL" ? undefined : selectedType,
          sortBy: sortBy || undefined,
          direction: direction || undefined,
          showDeleted
        }
      });
      setPaginationData(response.data);
    } catch (error) {
      toast.error(error.message || "Failed to load documents");
    }
  }, [api, globalProjectId, page, pageSize, searchTerm, dateRange, selectedUser, selectedType, sortBy, direction, showDeleted, setPaginationData]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const confirm = useConfirm();

  // Delete photo/document
  const deletePhoto = async (id) => {
    const isConfirmed = await confirm({
      title: "Delete Document",
      message: "Are you sure you want to delete this document?"
    });
    if (!isConfirmed) return;
    try {
      await api.delete(`/photos/${id}`);
      toast.success("Document deleted");
      loadPhotos();
    } catch (error) {
      toast.error(error.message || "Failed to delete document");
    }
  };

  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerData, setViewerData] = useState("");

  const openViewer = (data) => {
    setViewerData(data);
    setIsViewerOpen(true);
  };

  const closeViewer = () => {
    setIsViewerOpen(false);
    setViewerData("");
  };
  const handleClearAllFilters = () => {
    setSearchTerm("");
    setDateRange({ start: "", end: "" });
    setSelectedUser("ALL");
    setSelectedType("ALL");
    setSortBy("createdAt");
    setDirection("desc");
    setShowDeleted(false);
    setPage(0);
  };

  return (
    <>
      <div className="flex-1 flex flex-col animate-in fade-in duration-500">
        <div className="flex-1 space-y-6">
          <PageHeader
            title="Site Documents"
            subtitle="Capture and manage construction site documents"
            actionLabel={user?.permissions?.document?.create ? "Upload" : null}
            onActionClick={() => setIsUploadOpen(true)}
            icon={Camera}
            pageIcon={Camera}
            extraActions={
              <div className="flex items-center gap-3">
                {(searchTerm ||
                  dateRange.start ||
                  dateRange.end ||
                  selectedUser !== "ALL" ||
                  selectedType !== "ALL" ||
                  sortBy !== "createdAt" ||
                  direction !== "desc" ||
                  showDeleted) && (
                  <Button
                    variant="ghost"
                    onClick={handleClearAllFilters}
                    className="h-10 px-3 text-slate-500 hover:text-primary hover:bg-slate-100 dark:hover:bg-yale-blue-800/30 flex items-center gap-2"
                  >
                    <RotateCcw className="w-4 h-4" />
                    <span className="hidden sm:inline">Clear All</span>
                  </Button>
                )}
                <SearchInput
                  placeholder="Search documents..."
                  className="md:w-72"
                  value={searchTerm}
                  onSearch={(val) => {
                    setSearchTerm(val);
                    setPage(0);
                  }}
                />
              </div>
            }
          />

          <DocumentDialog
            isOpen={isUploadOpen}
            onOpenChange={setIsUploadOpen}
            selectedProject={globalProjectId === "ALL" ? "" : globalProjectId}
            onSuccess={loadPhotos}
          />

          <DocumentTable
            documents={photos}
            permissions={{
              canDelete: user?.permissions?.document?.delete
            }}
            onDelete={deletePhoto}
            onView={openViewer}
            dateRange={dateRange}
            onDateRangeChange={(range) => {
              setDateRange(range);
              setPage(0);
            }}
            selectedUser={selectedUser}
            onUserChange={(userId) => {
              setSelectedUser(userId);
              setPage(0);
            }}
            selectedType={selectedType}
            onTypeChange={(type) => {
              setSelectedType(type);
              setPage(0);
            }}
            sortBy={sortBy}
            direction={direction}
            onSort={(key, dir) => {
              setSortBy(key);
              setDirection(dir);
              setPage(0);
            }}
            showDeleted={showDeleted}
            setShowDeleted={setShowDeleted}
            setCurrentPage={setPage}
          />
        </div>

        <div className="mt-8">
          <Pagination pagination={pagination} />
        </div>
      </div>

      <DocumentViewer isOpen={isViewerOpen} onClose={closeViewer} data={viewerData} />
    </>
  );
};

export default Photos;
